import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { cacheDashboard, getOfflineDashboard } from '../services/db';
import {
  AreaChart, Area, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../api/axios';
import Layout from '../components/Layout';
import { SkeletonCard } from '../components/Skeleton';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';
import { useInView } from '../hooks/useInView';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { formatCurrency as fmt, formatCurrencyCompact as fmtK } from '../utils/currency';
import { getBusinessTypeMeta } from '../utils/businessProfiles';

const MONTHS = ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

const groupByMonth = (transactions) => {
  const map = Object.fromEntries(MONTHS.map((month) => [month, { month, revenue: 0, expenses: 0 }]));

  transactions.forEach((transaction) => {
    const month = new Date(transaction.date).toLocaleString('default', { month: 'short' });
    if (map[month]) {
      if (transaction.type === 'income') map[month].revenue += parseFloat(transaction.amount);
      else map[month].expenses += parseFloat(transaction.amount);
    }
  });

  return Object.values(map).map((month) => ({
    ...month,
    profit: month.revenue - month.expenses,
  }));
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div style={{
      background: 'white',
      border: '1px solid #E2E8F0',
      borderRadius: 10,
      padding: '10px 14px',
      fontSize: '0.82rem',
      boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: '#0F172A', fontFamily: 'Sora, sans-serif' }}>{label}</div>
      {payload.map((point) => (
        <div key={point.name} style={{ color: point.color, marginBottom: 3 }}>
          {point.name}: <strong>{fmt(point.value)}</strong>
        </div>
      ))}
    </div>
  );
};

function Sparkline({ color }) {
  const points = [30, 45, 35, 55, 42, 68, 58, 75, 65, 82];
  const maxY = Math.max(...points);
  const width = 64;
  const height = 28;
  const coordinates = points.map((value, index) => [
    (index / (points.length - 1)) * width,
    height - (value / maxY) * height,
  ]);

  return (
    <svg width={width} height={height} style={{ opacity: 0.6 }}>
      <polyline
        points={coordinates.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function KpiCard({ label, value, icon, accentColor, iconBg, delay = 0, trend }) {
  const count = useAnimatedCounter(value, 1400);

  return (
    <div className="kpi-card" style={{ borderLeftColor: accentColor, animationDelay: `${delay}ms` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="kpi-label">{label}</div>
          <div className="kpi-value" style={{ color: accentColor, marginTop: 6 }}>
            {fmtK(count)}
          </div>
          {trend !== undefined && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              marginTop: 6,
              fontSize: '0.7rem',
              fontWeight: 700,
              color: trend >= 0 ? '#16A34A' : '#DC2626',
              background: trend >= 0 ? '#F0FDF4' : '#FEF2F2',
              padding: '2px 7px',
              borderRadius: 20,
            }}>
              {trend >= 0 ? 'UP' : 'DOWN'} {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <div className="kpi-icon" style={{ background: iconBg, color: accentColor }}>
            {icon}
          </div>
          <Sparkline color={accentColor} />
        </div>
      </div>
    </div>
  );
}

function LowStockItem({ item, delay }) {
  const percentage = Math.min((item.quantity / 10) * 100, 100);
  const barColor = percentage < 20 ? '#EF4444' : percentage < 50 ? '#F59E0B' : '#22C55E';

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '10px 0',
      borderBottom: '1px solid #F1F4F9',
      animation: 'slideUp 350ms ease forwards',
      animationDelay: `${delay}ms`,
      opacity: 0,
    }}>
      <div style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        background: item.quantity === 0 ? '#FEE2E2' : '#FEF3C7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.72rem',
        fontWeight: 800,
        color: item.quantity === 0 ? '#991B1B' : '#92400E',
        flexShrink: 0,
      }}>
        SKU
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 600,
          fontSize: '0.875rem',
          color: '#0F172A',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {item.name}
        </div>
        <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, height: 5, borderRadius: 3, background: '#E2E8F0', overflow: 'hidden' }}>
            <div style={{ width: `${percentage}%`, height: '100%', borderRadius: 3, background: barColor, transition: 'width 0.6s ease' }} />
          </div>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: barColor, minWidth: 42 }}>
            {item.quantity === 0 ? 'Out' : `${item.quantity} left`}
          </span>
        </div>
      </div>
    </div>
  );
}

const AVATAR_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#EF4444', '#06B6D4'];

function CustomerAvatar({ name, index }) {
  const initials = (name || 'Walk In').split(' ').map((part) => part[0]).slice(0, 2).join('').toUpperCase();
  const background = AVATAR_COLORS[index % AVATAR_COLORS.length];

  return (
    <div style={{
      width: 34,
      height: 34,
      borderRadius: '50%',
      background,
      color: 'white',
      fontSize: '0.7rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      fontFamily: 'Sora, sans-serif',
    }}>
      {initials}
    </div>
  );
}

const PULSE_TONES = {
  amber: { bg: '#FFF7ED', border: '#FED7AA', title: '#9A3412', body: '#C2410C' },
  red: { bg: '#FEF2F2', border: '#FECACA', title: '#991B1B', body: '#B91C1C' },
  green: { bg: '#ECFDF5', border: '#BBF7D0', title: '#166534', body: '#15803D' },
  blue: { bg: '#EFF6FF', border: '#BFDBFE', title: '#1D4ED8', body: '#2563EB' },
  purple: { bg: '#F5F3FF', border: '#DDD6FE', title: '#6D28D9', body: '#7C3AED' },
  slate: { bg: '#F8FAFC', border: '#E2E8F0', title: '#334155', body: '#64748B' },
};

const PAYMENT_METHOD_LABELS = {
  cash: 'Cash',
  momo: 'MoMo',
  card: 'Card',
  credit: 'Credit',
};

export default function Dashboard() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const canViewFinance = can('finances:view');
  const canViewAi = can('ai:view');
  const canViewCrm = can('crm:view');
  const canViewCustomers = can('customers:view');
  const dashboardCacheKey = `${user?.id || 'guest'}:${canViewFinance ? 'finance' : 'ops'}`;
  const [allTransactions, setAllTransactions] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [summary, setSummary] = useState({
    totalRevenue: 0,
    grossProfit: 0,
    operatingExpenses: 0,
    netProfit: 0,
    totalItems: 0,
    totalCustomers: 0,
    recentSales: 0,
  });
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartTab, setChartTab] = useState('6M');
  const [invAlerts, setInvAlerts] = useState({ expired: 0, critical: 0, reorder: 0 });
  const [crmOverview, setCrmOverview] = useState(null);
  const [financialHealth, setFinancialHealth] = useState(null);
  const [aiHealth, setAiHealth] = useState(null);
  const [isOfflineData, setIsOfflineData] = useState(false);
  const [workspaceProfile, setWorkspaceProfile] = useState({ name: '', businessType: 'retail' });
  const { isOnline } = useOnlineStatus();

  const [salesRef, salesInView] = useInView();
  const [stockRef, stockInView] = useInView();

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      if (!isOnline) {
        const cached = await getOfflineDashboard(dashboardCacheKey);
        if (cached) {
          setSummary({
            totalRevenue: cached.totalRevenue ?? 0,
            grossProfit: cached.grossProfit ?? 0,
            operatingExpenses: cached.operatingExpenses ?? 0,
            netProfit: cached.netProfit ?? 0,
            totalItems: cached.totalItems ?? 0,
            totalCustomers: cached.totalCustomers ?? 0,
            recentSales: cached.recentSales ?? 0,
          });
          setIsOfflineData(true);
        }
        setLoading(false);
        return;
      }

      try {
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear();

        const [
          txRes,
          sumRes,
          invRes,
          salesRes,
          customersRes,
          expiryRes,
          reorderRes,
          crmRes,
          profitLossRes,
          budgetsRes,
          aiRes,
          workspaceRes,
        ] = await Promise.all([
          canViewFinance ? api.get('/finances/transactions') : Promise.resolve({ data: [] }),
          canViewFinance ? api.get('/finances/summary?period=year') : Promise.resolve({ data: {} }),
          api.get('/inventory'),
          api.get('/sales'),
          canViewCustomers ? api.get('/customers') : Promise.resolve({ data: [] }),
          api.get('/inventory-alerts/expiring?days=30').catch(() => ({ data: {} })),
          api.get('/inventory-alerts/reorder').catch(() => ({ data: { items: [] } })),
          canViewCrm ? api.get('/crm/overview').catch(() => ({ data: null })) : Promise.resolve({ data: null }),
          canViewFinance ? api.get(`/financials/pl?month=${currentMonth}&year=${currentYear}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
          canViewFinance ? api.get(`/financials/budgets?month=${currentMonth}&year=${currentYear}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
          canViewAi ? api.get('/ai/health-score').catch(() => ({ data: null })) : Promise.resolve({ data: null }),
          api.get('/workspaces/current/profile').catch(() => ({ data: null })),
        ]);

        setAllTransactions(txRes.data);

        const newSummary = {
          totalRevenue: sumRes.data.totalRevenue ?? 0,
          grossProfit: sumRes.data.grossProfit ?? 0,
          operatingExpenses: sumRes.data.operatingExpenses ?? 0,
          netProfit: sumRes.data.netProfit ?? 0,
          totalItems: invRes.data?.length ?? sumRes.data.totalItems ?? 0,
          totalCustomers: customersRes.data?.length ?? sumRes.data.totalCustomers ?? 0,
          recentSales: salesRes.data?.length ?? sumRes.data.recentSales ?? 0,
        };

        setSummary(newSummary);
        cacheDashboard(dashboardCacheKey, newSummary).catch(() => {});

        setLowStock(invRes.data.filter((item) => item.quantity <= 5));
        setRecentSales(
          [...salesRes.data]
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 6),
        );

        const expiryData = expiryRes.data || {};
        setInvAlerts({
          expired: (expiryData.expired || []).length,
          critical: (expiryData.critical || []).length,
          reorder: reorderRes.data?.totalCount || 0,
        });

        if (crmRes.data) setCrmOverview(crmRes.data);

        if (profitLossRes.data) {
          const budgetOverruns = (budgetsRes.data?.budgets || []).filter(
            (budget) => budget.status === 'over' && budget.category !== 'Revenue',
          );

          setFinancialHealth({
            pl: profitLossRes.data,
            budgetOverruns,
            revBudget: budgetsRes.data?.revenueBudget || 0,
            hasBudgets: (budgetsRes.data?.budgets || []).length > 0,
          });
        }

        if (aiRes.data) setAiHealth(aiRes.data);

        if (workspaceRes.data) {
          setWorkspaceProfile({
            name: workspaceRes.data.name || '',
            businessType: workspaceRes.data.businessType || 'retail',
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOnline, canViewAi, canViewCrm, canViewCustomers, canViewFinance, dashboardCacheKey]);

  const chartData = groupByMonth(allTransactions);
  const monthYear = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
  const businessMeta = getBusinessTypeMeta(workspaceProfile.businessType);
  const dashboardIntro = {
    retail: 'Sales. Stock. Cashflow.',
    wholesale: 'Orders. Margins. Collections.',
    retail_wholesale: 'Counter sales and trade flow.',
    other: 'Sales. Stock. Money.',
  }[workspaceProfile.businessType] || 'Sales. Stock. Money.';
  const safeDashboardIntro = canViewFinance ? dashboardIntro : 'Sales. Stock. Daily actions.';

  const quickActions = {
    retail: [
      { label: 'Quick sale', href: '/pos', tint: '#EFF6FF', color: '#1D4ED8', permission: 'pos:use' },
      { label: 'Restock', href: '/inventory/intelligence?tab=reorder', tint: '#FFF7ED', color: '#C2410C', permission: 'inventory:activity:view' },
      { label: 'Collect', href: '/debts', tint: '#FEF2F2', color: '#B91C1C', permission: 'debt:view' },
      { label: 'Customers', href: '/customers', tint: '#F5F3FF', color: '#6D28D9', permission: 'customers:view' },
    ],
    wholesale: [
      { label: 'Bulk sale', href: '/sales', tint: '#EFF6FF', color: '#1D4ED8', permission: 'sales:view' },
      { label: 'Restock', href: '/inventory/intelligence?tab=reorder', tint: '#FFF7ED', color: '#C2410C', permission: 'inventory:activity:view' },
      { label: 'Collect', href: '/debts', tint: '#FEF2F2', color: '#B91C1C', permission: 'debt:view' },
      { label: 'Suppliers', href: '/suppliers', tint: '#ECFDF5', color: '#047857', permission: 'purchases:view' },
    ],
    retail_wholesale: [
      { label: 'Quick sale', href: '/pos', tint: '#EFF6FF', color: '#1D4ED8', permission: 'pos:use' },
      { label: 'Trade sales', href: '/sales', tint: '#F5F3FF', color: '#6D28D9', permission: 'sales:view' },
      { label: 'Restock', href: '/inventory/intelligence?tab=reorder', tint: '#FFF7ED', color: '#C2410C', permission: 'inventory:activity:view' },
      { label: 'Collect', href: '/debts', tint: '#FEF2F2', color: '#B91C1C', permission: 'debt:view' },
    ],
    other: [
      { label: 'View sales', href: '/sales', tint: '#EFF6FF', color: '#1D4ED8', permission: 'sales:view' },
      { label: 'Stock', href: '/inventory', tint: '#FFF7ED', color: '#C2410C', permission: 'inventory:view' },
      { label: 'Customers', href: '/customers', tint: '#F5F3FF', color: '#6D28D9', permission: 'customers:view' },
      { label: 'Reports', href: '/reports', tint: '#ECFDF5', color: '#047857', permission: 'reports:view' },
    ],
  }[workspaceProfile.businessType] || [];
  const visibleQuickActions = quickActions.filter((action) => !action.permission || can(action.permission));

  const statusColor = {
    completed: { label: 'Paid', bg: '#D1FAE5', color: '#065F46' },
    unpaid: { label: 'Unpaid', bg: '#FEE2E2', color: '#991B1B' },
    partial: { label: 'Partial', bg: '#FEF3C7', color: '#92400E' },
    pending: { label: 'Pending', bg: '#FEF3C7', color: '#92400E' },
    cancelled: { label: 'Cancelled', bg: '#F1F5F9', color: '#475569' },
  };

  const pulseItems = [];
  if (invAlerts.reorder > 0) {
    pulseItems.push({
      key: 'reorder',
      tone: 'amber',
      icon: 'PO',
      title: `${invAlerts.reorder} item${invAlerts.reorder > 1 ? 's' : ''} need restocking`,
      href: '/inventory/intelligence?tab=reorder',
      action: 'Restock',
    });
  }
  if (invAlerts.expired > 0) {
    pulseItems.push({
      key: 'expired',
      tone: 'red',
      icon: 'EXP',
      title: `${invAlerts.expired} expired item${invAlerts.expired > 1 ? 's are' : ' is'} still on shelf`,
      href: '/inventory/intelligence?tab=expiry',
      action: 'Review',
    });
  }
  if (crmOverview?.atRiskCount > 0) {
    pulseItems.push({
      key: 'at-risk-customers',
      tone: 'purple',
      icon: 'CRM',
      title: `${crmOverview.atRiskCount} customer${crmOverview.atRiskCount > 1 ? 's are' : ' is'} going quiet`,
      href: '/crm?tab=sms',
      action: 'Reach out',
    });
  } else if (crmOverview?.vipCount > 0) {
    pulseItems.push({
      key: 'vip-customers',
      tone: 'green',
      icon: 'VIP',
      title: `${crmOverview.vipCount} VIP customer${crmOverview.vipCount > 1 ? 's are' : ' is'} active`,
      href: '/crm?tab=segments',
      action: 'View VIPs',
    });
  }
  if (aiHealth?.recommendations?.[0]) {
    pulseItems.push({
      key: 'ai-priority',
      tone: aiHealth.overallScore >= 75 ? 'green' : aiHealth.overallScore >= 60 ? 'amber' : 'red',
      icon: 'AI',
      title: aiHealth.recommendations[0].title,
      href: '/ai?tab=health',
      action: 'Open AI',
    });
  }
  if (financialHealth?.budgetOverruns?.length > 0) {
    pulseItems.push({
      key: 'budget-overrun',
      tone: 'blue',
      icon: 'BGT',
      title: `${financialHealth.budgetOverruns.length} budget categor${financialHealth.budgetOverruns.length > 1 ? 'ies are' : 'y is'} over limit`,
      href: '/financials?tab=budgets',
      action: 'Check budgets',
    });
  }

  const heroStats = [
    { label: 'Low-stock items', value: lowStock.length, accent: lowStock.length > 0 ? '#F0C040' : '#34D399' },
    { label: workspaceProfile.businessType === 'wholesale' ? 'Trade customers' : 'Customers', value: summary.totalCustomers, accent: '#7DD3FC' },
    { label: 'Products tracked', value: summary.totalItems, accent: '#C4B5FD' },
  ];

  const visiblePulseItems = pulseItems.slice(0, 4);
  const latestAiRecommendation = aiHealth?.recommendations?.[0] || null;
  const revenueTargetProgress = financialHealth?.revBudget > 0
    ? Math.min(100, (financialHealth.pl.revenue / financialHealth.revBudget) * 100)
    : null;

  const navigate = useNavigate();
  const todaySales = recentSales.filter(
    (s) => new Date(s.createdAt).toDateString() === new Date().toDateString(),
  ).length;
  const topExpenses = (() => {
    const map = {};
    allTransactions.filter((t) => t.type === 'expense').forEach((t) => {
      const cat = t.category || 'Other';
      map[cat] = (map[cat] || 0) + parseFloat(t.amount || 0);
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 4);
    const total = sorted.reduce((sum, [, v]) => sum + v, 0);
    return sorted.map(([cat, val]) => ({ cat, val, pct: total > 0 ? (val / total) * 100 : 0 }));
  })();
  const healthScore = aiHealth?.overallScore || 0;
  const healthDimensions = aiHealth?.dimensions || {};
  const healthInsight = aiHealth?.recommendations?.[0]?.title || null;

  return (
    <Layout>
      <div style={{
        padding: '28px 32px',
        minHeight: '100%',
        background: '#0B0E17',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Background orbs */}
        <div style={{ position: 'absolute', top: -200, right: -200, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(240,180,41,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: -200, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,201,177,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />
        {/* Grid texture */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)', backgroundSize: '48px 48px', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#00C9B1', boxShadow: '0 0 6px #00C9B1' }} />
                <span style={{ fontSize: 10, color: '#00C9B1', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Live</span>
                {isOfflineData && (
                  <span style={{ fontSize: 9, color: '#F0B429', background: 'rgba(240,180,41,0.1)', padding: '2px 8px', borderRadius: 20, marginLeft: 4, fontWeight: 700 }}>Offline cache</span>
                )}
              </div>
              <h1 style={{ fontFamily: '"Instrument Serif", serif', fontStyle: 'italic', fontSize: '1.75rem', color: '#F0B429', margin: 0, lineHeight: 1.1 }}>
                {workspaceProfile.name || 'Your workspace'}
              </h1>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {['7D', '30D', '90D'].map((tab) => (
                <button key={tab} onClick={() => setChartTab(tab)} style={{
                  padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)',
                  background: chartTab === tab ? 'rgba(240,180,41,0.15)' : 'transparent',
                  color: chartTab === tab ? '#F0B429' : 'rgba(255,255,255,0.45)',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: '150ms',
                }}>{tab}</button>
              ))}
              {can('pos:use') && (
                <button onClick={() => navigate('/pos')} style={{
                  padding: '7px 18px', borderRadius: 8, border: 'none',
                  background: 'linear-gradient(135deg, #F0B429, #FFCD3C)', color: '#0B0E17',
                  fontSize: 13, fontWeight: 800, cursor: 'pointer', letterSpacing: '0.01em',
                  fontFamily: '"Cabinet Grotesk", sans-serif',
                }}>+ New Sale</button>
              )}
            </div>
          </div>

          {loading ? (
            <div style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: 60, fontSize: 14 }}>Loading dashboard…</div>
          ) : (
            <>
              {/* Hero stats band */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 20 }}>
                <div style={{ background: 'linear-gradient(135deg, rgba(240,180,41,0.12), rgba(240,180,41,0.04))', padding: '24px 28px', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, fontWeight: 700 }}>Revenue</div>
                  <div style={{ fontSize: '1.85rem', fontWeight: 900, fontFamily: '"Cabinet Grotesk", sans-serif', background: 'linear-gradient(135deg, #F0B429, #FFCD3C)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {fmtK(summary.totalRevenue)}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>12-month total</div>
                </div>
                <div style={{ background: 'rgba(0,201,177,0.06)', padding: '24px 28px', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, fontWeight: 700 }}>Net Profit</div>
                  <div style={{ fontSize: '1.85rem', fontWeight: 900, fontFamily: '"Cabinet Grotesk", sans-serif', color: summary.netProfit >= 0 ? '#00C9B1' : '#FF6B6B' }}>
                    {fmtK(summary.netProfit)}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>{summary.netProfit >= 0 ? 'Profitable' : 'Operating at a loss'}</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '24px 28px', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, fontWeight: 700 }}>Sales Today</div>
                  <div style={{ fontSize: '1.85rem', fontWeight: 900, fontFamily: '"Cabinet Grotesk", sans-serif', color: '#FFFFFF' }}>
                    {todaySales}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>transactions</div>
                </div>
                <div style={{ background: 'rgba(255,107,107,0.06)', padding: '24px 28px' }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, fontWeight: 700 }}>Expenses</div>
                  <div style={{ fontSize: '1.85rem', fontWeight: 900, fontFamily: '"Cabinet Grotesk", sans-serif', color: '#FF6B6B' }}>
                    {fmtK(summary.operatingExpenses)}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>operating costs</div>
                </div>
              </div>

              {/* Row 1 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                {/* Revenue vs Profit bar chart */}
                {canViewFinance ? (
                  <div style={{ background: '#1E2235', borderRadius: 14, border: '1px solid rgba(255,255,255,0.06)', padding: '20px 20px 16px' }}>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 4 }}>Trendline</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: 'rgba(255,255,255,0.9)', fontFamily: '"Cabinet Grotesk", sans-serif' }}>Revenue vs Profit</div>
                    </div>
                    {(() => {
                      const maxVal = Math.max(...chartData.map((d) => Math.max(d.revenue, 0)), 1);
                      const barW = 16;
                      const gap = 6;
                      const colW = barW * 2 + gap + 6;
                      const totalW = chartData.length * colW;
                      const chartH = 110;
                      return (
                        <svg width="100%" viewBox={`0 0 ${totalW} ${chartH + 22}`} preserveAspectRatio="none" style={{ overflow: 'visible' }}>
                          {chartData.map((d, i) => {
                            const x = i * colW;
                            const revH = (d.revenue / maxVal) * chartH;
                            const profH = Math.abs(d.profit / maxVal) * chartH;
                            const profPositive = d.profit >= 0;
                            return (
                              <g key={i}>
                                <rect x={x} y={chartH - revH} width={barW} height={revH} rx={3} fill="rgba(240,180,41,0.7)" />
                                <rect x={x + barW + 2} y={profPositive ? chartH - profH : chartH} width={barW} height={profH} rx={3} fill={profPositive ? '#00C9B1' : '#FF6B6B'} />
                                <text x={x + barW} y={chartH + 14} textAnchor="middle" fontSize={8} fill="rgba(255,255,255,0.3)">{d.month}</text>
                              </g>
                            );
                          })}
                        </svg>
                      );
                    })()}
                    <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
                      {[{ color: 'rgba(240,180,41,0.7)', label: 'Revenue' }, { color: '#00C9B1', label: 'Profit' }].map(({ color, label }) => (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ background: '#1E2235', borderRadius: 14, border: '1px solid rgba(255,255,255,0.06)', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 13 }}>Finance access required</div>
                  </div>
                )}

                {/* Top Expenses donut */}
                <div style={{ background: '#1E2235', borderRadius: 14, border: '1px solid rgba(255,255,255,0.06)', padding: '20px' }}>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 4 }}>Breakdown</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: 'rgba(255,255,255,0.9)', fontFamily: '"Cabinet Grotesk", sans-serif' }}>Top Expenses</div>
                  </div>
                  {topExpenses.length > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      {(() => {
                        const colors = ['#FF6B6B', '#F0B429', '#A78BFA', '#38BDF8'];
                        const r = 38;
                        const cx = 50;
                        const cy = 50;
                        let cumPct = 0;
                        const total = topExpenses.reduce((s, e) => s + e.val, 0);
                        return (
                          <svg width={100} height={100} viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
                            {topExpenses.map((exp, i) => {
                              const pct = exp.val / total;
                              const startAngle = cumPct * 2 * Math.PI - Math.PI / 2;
                              const endAngle = (cumPct + pct) * 2 * Math.PI - Math.PI / 2;
                              cumPct += pct;
                              const x1 = cx + r * Math.cos(startAngle);
                              const y1 = cy + r * Math.sin(startAngle);
                              const x2 = cx + r * Math.cos(endAngle);
                              const y2 = cy + r * Math.sin(endAngle);
                              const largeArc = pct > 0.5 ? 1 : 0;
                              return (
                                <path
                                  key={i}
                                  d={`M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`}
                                  fill={colors[i % colors.length]}
                                  opacity={0.85}
                                />
                              );
                            })}
                            <circle cx={cx} cy={cy} r={24} fill="#1E2235" />
                            <text x={cx} y={cy + 4} textAnchor="middle" fontSize={11} fill="rgba(255,255,255,0.6)" fontWeight="700">{topExpenses.length}</text>
                            <text x={cx} y={cy + 16} textAnchor="middle" fontSize={7} fill="rgba(255,255,255,0.3)">cats</text>
                          </svg>
                        );
                      })()}
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
                        {topExpenses.map((exp, i) => {
                          const colors = ['#FF6B6B', '#F0B429', '#A78BFA', '#38BDF8'];
                          return (
                            <div key={i}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{exp.cat}</span>
                                <span style={{ color: colors[i % colors.length], fontWeight: 700 }}>{exp.pct.toFixed(0)}%</span>
                              </div>
                              <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.06)' }}>
                                <div style={{ width: `${exp.pct}%`, height: '100%', borderRadius: 2, background: colors[i % colors.length] }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 13, textAlign: 'center', padding: '28px 0' }}>No expense data</div>
                  )}
                </div>

                {/* Quick Actions + stock stats */}
                <div style={{ background: '#1E2235', borderRadius: 14, border: '1px solid rgba(255,255,255,0.06)', padding: '20px' }}>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 4 }}>Actions</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: 'rgba(255,255,255,0.9)', fontFamily: '"Cabinet Grotesk", sans-serif' }}>Quick links</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                    {visibleQuickActions.slice(0, 4).map((action) => (
                      <a key={action.label} href={action.href} style={{
                        display: 'flex', flexDirection: 'column', gap: 4, padding: '10px 12px',
                        background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)',
                        textDecoration: 'none',
                      }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>{action.label}</span>
                        <span style={{ fontSize: 10, color: '#F0B429' }}>Open →</span>
                      </a>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[
                      { label: 'Products', value: summary.totalItems, color: '#A78BFA' },
                      { label: 'Customers', value: summary.totalCustomers, color: '#38BDF8' },
                      { label: 'Low stock', value: lowStock.length, color: lowStock.length > 0 ? '#FF6B6B' : '#00C9B1' },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ flex: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.1rem', fontWeight: 900, color, fontFamily: '"Cabinet Grotesk", sans-serif' }}>{value}</div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Row 2 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                {/* Recent Sales */}
                <div style={{ background: '#1E2235', borderRadius: 14, border: '1px solid rgba(255,255,255,0.06)', padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 4 }}>Sell fast</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: 'rgba(255,255,255,0.9)', fontFamily: '"Cabinet Grotesk", sans-serif' }}>Recent Sales</div>
                    </div>
                    <a href="/sales" style={{ fontSize: 11, color: '#F0B429', fontWeight: 700, textDecoration: 'none' }}>View all →</a>
                  </div>
                  {recentSales.length === 0 ? (
                    <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 13, textAlign: 'center', padding: '28px 0' }}>No sales yet</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {recentSales.map((sale, index) => (
                        <div key={sale.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: index < recentSales.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                          <CustomerAvatar name={sale.customer?.name} index={index} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {sale.customer?.name || 'Walk-in'}
                            </div>
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>
                              {sale.items?.length || 0} item{sale.items?.length !== 1 ? 's' : ''} · {PAYMENT_METHOD_LABELS[sale.paymentMethod] || 'Sale'}
                            </div>
                          </div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#00C9B1', fontFamily: '"Cabinet Grotesk", sans-serif' }}>{fmt(sale.total)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Inventory Alerts */}
                <div style={{ background: '#1E2235', borderRadius: 14, border: '1px solid rgba(255,255,255,0.06)', padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 4 }}>Stock risk</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: 'rgba(255,255,255,0.9)', fontFamily: '"Cabinet Grotesk", sans-serif' }}>Inventory</div>
                    </div>
                    <a href="/inventory/intelligence?tab=reorder" style={{ fontSize: 11, color: '#F0B429', fontWeight: 700, textDecoration: 'none' }}>Open →</a>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    {[
                      { label: 'Reorder', value: invAlerts.reorder, color: '#F0B429' },
                      { label: 'Expired', value: invAlerts.expired, color: '#FF6B6B' },
                      { label: 'Critical', value: invAlerts.critical, color: '#FF6B6B' },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ flex: 1, background: value > 0 ? 'rgba(255,107,107,0.08)' : 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '8px 10px', textAlign: 'center', border: `1px solid ${value > 0 ? 'rgba(255,107,107,0.2)' : 'rgba(255,255,255,0.06)'}` }}>
                        <div style={{ fontSize: '1.1rem', fontWeight: 900, color: value > 0 ? color : 'rgba(255,255,255,0.2)', fontFamily: '"Cabinet Grotesk", sans-serif' }}>{value}</div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                  {lowStock.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {lowStock.slice(0, 4).map((item) => {
                        const pct = Math.min((item.quantity / 10) * 100, 100);
                        const barColor = pct < 20 ? '#FF6B6B' : pct < 50 ? '#F0B429' : '#00C9B1';
                        return (
                          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', fontWeight: 600, marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                              <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.06)' }}>
                                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: barColor }} />
                              </div>
                            </div>
                            <span style={{ fontSize: 10, fontWeight: 700, color: barColor, minWidth: 30, textAlign: 'right' }}>{item.quantity === 0 ? 'Out' : item.quantity}</span>
                          </div>
                        );
                      })}
                      {lowStock.length > 4 && (
                        <a href="/inventory/intelligence?tab=reorder" style={{ fontSize: 10, color: '#F0B429', textDecoration: 'none', fontWeight: 700, marginTop: 2 }}>
                          +{lowStock.length - 4} more →
                        </a>
                      )}
                    </div>
                  ) : (
                    <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>Stock looks stable</div>
                  )}
                </div>

                {/* AI Health Score */}
                <div style={{ background: '#1E2235', borderRadius: 14, border: '1px solid rgba(255,255,255,0.06)', padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 4 }}>AI health</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: 'rgba(255,255,255,0.9)', fontFamily: '"Cabinet Grotesk", sans-serif' }}>Business Health</div>
                    </div>
                    {canViewAi && <a href="/ai?tab=health" style={{ fontSize: 11, color: '#F0B429', fontWeight: 700, textDecoration: 'none' }}>Open AI →</a>}
                  </div>
                  {canViewAi && aiHealth ? (
                    <>
                      {(() => {
                        const score = healthScore;
                        const arcColor = score >= 75 ? '#00C9B1' : score >= 60 ? '#F0B429' : '#FF6B6B';
                        const r = 36;
                        const cx = 50;
                        const cy = 54;
                        const circumference = Math.PI * r;
                        const filled = (score / 100) * circumference;
                        return (
                          <div style={{ textAlign: 'center', marginBottom: 14 }}>
                            <svg width={100} height={64} viewBox="0 0 100 64" style={{ overflow: 'visible' }}>
                              <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={8} strokeLinecap="round" />
                              <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke={arcColor} strokeWidth={8} strokeLinecap="round" strokeDasharray={`${filled} ${circumference}`} />
                              <text x={cx} y={cy - 6} textAnchor="middle" fontSize={18} fontWeight="900" fill={arcColor} fontFamily='"Cabinet Grotesk", sans-serif'>{score}</text>
                              <text x={cx} y={cy + 8} textAnchor="middle" fontSize={8} fill="rgba(255,255,255,0.3)">/100</text>
                            </svg>
                            <div style={{ fontSize: 12, fontWeight: 700, color: arcColor }}>{aiHealth.label || 'Health Score'}</div>
                          </div>
                        );
                      })()}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {Object.entries(healthDimensions).slice(0, 4).map(([dim, val]) => {
                          const pct = typeof val === 'number' ? val : val?.score || 0;
                          const dimColor = pct >= 75 ? '#00C9B1' : pct >= 60 ? '#F0B429' : '#FF6B6B';
                          return (
                            <div key={dim}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                                <span style={{ textTransform: 'capitalize' }}>{dim}</span>
                                <span style={{ color: dimColor, fontWeight: 700 }}>{Math.round(pct)}</span>
                              </div>
                              <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.06)' }}>
                                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: dimColor }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {healthInsight && (
                        <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(240,180,41,0.06)', borderRadius: 8, border: '1px solid rgba(240,180,41,0.12)' }}>
                          <div style={{ fontSize: 10, color: '#F0B429', fontWeight: 700, marginBottom: 3 }}>Top recommendation</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.4 }}>{healthInsight}</div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 13, textAlign: 'center', padding: '36px 0' }}>
                      {canViewAi ? 'Waiting for AI data' : 'AI access required'}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
